import { ButtonProps, extendTheme, ThemeConfig } from "@chakra-ui/react";
import {
  createBreakpoints,
  getColor,
  lighten,
  mode,
} from "@chakra-ui/theme-tools";
import { Global } from "@emotion/react";
import React from "react";
import type { GlobalStyleProps, Styles } from "@chakra-ui/theme-tools";

export const Fonts = () => (
  <Global
    styles={`
    html {
      font-size:16px;
    }
    @font-face {
      font-family: 'Chromatica';
      src: url('${process.env.REACT_APP_BASE_URL}fonts/ChromaticaModifiee.woff') format('woff');
    }
    @font-face {
      font-family: 'ChromaticaBold';
      src: url('${process.env.REACT_APP_BASE_URL}fonts/ChromaticaModifiee-Bold.woff') format('woff');
    }
    @font-face {
      font-family: 'ChromaticaMedium';
      src: url('${process.env.REACT_APP_BASE_URL}fonts/ChromaticaModifiee-Medium.woff') format('woff');
    }
      `}
  />
);

/*
[
  0 > px, > 480 px, > 748 px, > 992 px]

*/

const breakpoints = createBreakpoints({
  sm: "30em",
  md: "62em",
  lg: "80em",
  xl: "80em",
});

type GradientProps = {
  fromcolor: string;
  tocolor: string;
  bgcolor: string;
};

// setup light/dark mode global defaults
const styles: Styles = {
  global: (props) => ({
    body: {
      bg: mode("white", "#1F2023")(props),
    },
  }),
};

const config: ThemeConfig = {
  initialColorMode: "light",
  useSystemColorMode: false,
};

const primary = "#EEF05D";

const theme = extendTheme({
  config,
  colors: {
    black: "#131737",
    primary: primary,
    secondary: "#8676FF",
    purpleGradient: "#8D188B",
    blueGradient: "#39BBFA",
    textGrey: "#A1A1A6",
    textGreyDark: "#686868",
    cyan: {
      550: "#51B6C2",
    },
    gray: {
      550: "#828282",
    },
    error: "#f95928",
  },
  fonts: {
    body: "Chromatica,barlow,sans-serif",
    heading: "'Chromatica', sans-serif",
  },
  breakpoints,
  components: {
    Heading: {
      baseStyle: {
        fontWeight: "600",
      },
    },
    Text: {
      baseStyle: {
        fontWeight: "400",
      },
    },
    Link: {
      baseStyle: {
        transition: "ease-in-out all .2s",
        _hover: {
          color: "textGrey",
          textDecoration: "none",
        },
      },
    },
    Button: {
      baseStyle: {
        ">.chakra-button__icon": {
          fontSize: "24px",
        },
        "&.tile": {
          rounded: "md",
        },
        rounded: "full",
        fontWeight: 400,
      },
      variants: {
        gradient: (props: ButtonProps & GradientProps & { theme: any }) => {
          const { theme, bgcolor, fromcolor, tocolor } = props;

          const bgColor = getColor(theme, mode(bgcolor, bgcolor)(props));

          return {
            border: "3px solid",
            borderColor: "transparent",
            borderRadius: "full",
            background: `linear-gradient(${bgColor}, ${bgColor}) padding-box, 
            linear-gradient(135deg, ${fromcolor}, ${tocolor}) border-box`,
            "> *": {
              background: `linear-gradient(135deg, ${fromcolor}, ${tocolor})`,
              backgroundClip: "text",
              textFillColor: "transparent",
            },
            _hover: {
              background: `linear-gradient(${bgColor}, ${bgColor}) padding-box, 
              linear-gradient(315deg, ${fromcolor}, ${tocolor}) border-box`,
              "> *": {
                background: `linear-gradient(315deg, ${fromcolor}, ${tocolor})`,
                backgroundClip: "text",
              },
            },
          };
        },
      },
      colorScheme: {},
    },
    Modal: {
      baseStyle: {
        dialogContainer: {
          w: "100%"
        }
      }
    },
    Checkbox: {
      baseStyle: {
        icon: {
          color: "#000",
        },
        control: {
          border: "1px",
          borderRadius: "base",
          bg: "white",
          borderColor: "#000",
          _checked: {
            borderColor: "#3398A4",
            bg: "primary",
          },
          _hover: {
            bg: "primary",
          },
        },
      },
    },
  },
  styles,
});

export default theme;
